import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';

import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField/TextField';
import Typography from '@material-ui/core/Typography';
import { KeyboardDatePicker } from '@material-ui/pickers';
import MaterialUiPhoneNumber from 'material-ui-phone-number';
import moment from 'moment';

import ColorSelectField from './ColorSelectField';
import ImageUploadButton from './ImageUploadButton';
import PasswordTextField from './PasswordTextField';
import SelectField from './SelectField';
import SubdomainField from './SubdomainField';

const useStyles = makeStyles((theme) => ({
  field: {
    margin: theme.spacing(2, 0),
  },
  gridLabel: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  gridRow: {
    margin: theme.spacing(4, 0),
  },
  helperText: {
    position: 'absolute',
    top: '100%',
  },
  message: {
    color: '#999999',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '23px',
  },
  resetHelperText: {
    position: 'static',
  },
  rightHelperText: {
    left: 'calc(100% + 20px)',
    top: '50%',
    width: 'fit-content',
  },
}));

export default forwardRef(
  (
    {
      data,
      fields,
      allowEnterSubmit = false,
      onSave,
      saving,
      useLabel = true,
      useAutoFocus = true,
      labelPosition = 'left',
      validate = null,
      validationErrors = null,
      labelClass = null,
      resetData = false,
      onSetExternalError,
      showAllErrors = true,
      onValidationChange = null,
    },
    ref
  ) => {
    const classes = useStyles();

    const [editingData, setEditingData] = useState(null);
    const [editedFields, setEditedFields] = useState([]);
    const [errors, setErrors] = useState(validationErrors);
    const [showCurrentInputError, setShowCurrentInputError] = useState(false);
    const [currentInputName, setCurrentInputName] = useState(null);
    const [isFormValid, setIsFormValid] = useState(true);

    useEffect(() => {
      const defaultValue = {};
      if (data) {
        fields.forEach((field) => {
          // For dateOfBirth field, let's convert the time to current timezone while keeping other values (year, month, day, hour) the same
          // Note: need to subtract 1 from the second element in the array because Month is counted from 0 when parsing in Moment
          if (field.name === 'dateOfBirth') {
            if (data[field.name]) {
              defaultValue[field.name] = moment(
                moment
                  .parseZone(data[field.name])
                  .format('YYYY MM DD')
                  .split(' ')
                  .map(Number)
                  .map((e, i) => (i === 1 ? e - 1 : e))
              )
                .toDate()
                .toISOString();
            } else {
              defaultValue[field.name] = data[field.name];
            }
          } else if (field.defaultValue && data[field.name] === undefined) {
            defaultValue[field.name] = field.defaultValue;
          } else {
            defaultValue[field.name] = data[field.name];
          }
        });
      }

      setEditingData(defaultValue);
    }, [data]);

    useEffect(() => {
      setErrors(validationErrors);
    }, [validationErrors]);

    useEffect(() => {
      if (resetData) {
        setEditedFields([]);
        setErrors(null);
      }
    }, [resetData]);

    const validateFields = useCallback(
      async (updatedFields, displayError = false) => {
        let result = null;

        if (validate) {
          try {
            result = await validate(editingData, updatedFields);
          } catch (error) {
            result = { errors: { general: [error.message || 'Validation failed'] } };
          }
        }

        let isExternalErrorAvailable = false;
        Object.keys(validationErrors || {}).forEach((key) => {
          if (validationErrors[key]?.length) isExternalErrorAvailable = true;
        });

        const isValid = !result && !isExternalErrorAvailable;

        if (isValid) {
          setErrors({});
        } else {
          const combinedErrors = { ...result, ...validationErrors };
          setErrors(combinedErrors);
        }

        if (isFormValid !== isValid) {
          setIsFormValid(isValid);

          if (onValidationChange) {
            onValidationChange(isValid);
          }
        }

        if (displayError) {
          setShowCurrentInputError(true);
        }

        return isValid;
      },
      [validate, editingData, setErrors, validationErrors, isFormValid, onValidationChange]
    );

    const getValidDataInternal = useCallback(async () => {
      if (!editingData) {
        return null;
      }

      Object.keys(editingData).forEach((key) => {
        const field = fields.find((e) => e.name === key);
        if (typeof editingData[key] === 'string' && field && field.type !== 'password') {
          editingData[key] = editingData[key].trim();
        }
        if (field?.type === 'date' && field?.onlyDate) {
          editingData[key] = moment(editingData[key]).format('MM/DD/YYYY');
        }
      });

      const updatedFields = fields.map((field) => field.name);
      setEditedFields(updatedFields);

      const isValid = await validateFields(updatedFields);

      if (isValid) {
        return editingData;
      }

      return null;
    }, [editingData, fields, validateFields]);

    const setValue = useCallback(
      (fieldName, value) => {
        setEditingData((prevData) => {
          const newData = {
            ...prevData,
            [fieldName]: value,
          };

          return newData;
        });
      },
      [setEditingData]
    );

    const setFormData = useCallback(
      (value) => {
        setEditingData(value);
      },
      [setEditingData]
    );

    const getInternalData = useCallback(() => {
      return editingData;
    }, [editingData]);

    const getSpecificFieldsValidation = useCallback(
      async (selectedFields = [] || '') => {
        const updatedFields = typeof selectedFields === 'string' ? [selectedFields] : selectedFields;
        return validateFields(updatedFields);
      },
      [validateFields]
    );

    const getCurrentErrors = useCallback(() => {
      return errors;
    }, [errors]);

    const setInternalErrors = useCallback(
      (errObject) => {
        const externalErrors = {};
        Object.keys(errObject).forEach((key) => {
          externalErrors[key] = [errObject[key]];
        });
        setTimeout(() => {
          setErrors({ ...errors, ...externalErrors });
        }, 100);
      },
      [errors]
    );

    useImperativeHandle(ref, () => {
      return {
        getErrors: getCurrentErrors,
        getFormData: getInternalData,
        getSpecificFieldsValidation,
        getValidData: getValidDataInternal,
        setErrors: setInternalErrors,
        setFormData,
        setValue,
      };
    }, [getCurrentErrors, getValidDataInternal, setValue, setInternalErrors, setFormData, getInternalData, getSpecificFieldsValidation]);

    useEffect(() => {
      if (editedFields.length > 0) {
        validateFields(editedFields);
      }
    }, [validateFields, editedFields]);

    const handleTextChange = (fieldName, event) => {
      setShowCurrentInputError(false);
      setCurrentInputName(fieldName);
      setEditingData({
        ...editingData,
        [fieldName]: event.target.value,
      });

      // Note: getValidDataInternal is now async, but we can't await here
      // The validation will be handled by the blur event
      const changedField = fields.find((field) => field.name === fieldName);

      if (changedField && changedField.onChange) {
        changedField.onChange(event.target.value, true); // Pass true for now, real validation on blur
      }
    };

    const handlePhoneChange = (fieldName, value) => {
      setShowCurrentInputError(false);
      setCurrentInputName(fieldName);
      setEditingData({
        ...editingData,
        [fieldName]: value,
      });

      const changedField = fields.find((field) => field.name === fieldName);

      if (changedField && changedField.onChange) {
        changedField.onChange(value);
      }
    };

    const handleDateChange = (fieldName, value, onlyDate = false) => {
      setShowCurrentInputError(false);
      setCurrentInputName(fieldName);

      if (!value) {
        return;
      }

      if (!value.isValid()) {
        setEditingData({
          ...editingData,
          [fieldName]: value.toString(),
        });
        return;
      }

      const date = value.toDate();
      setEditingData({
        ...editingData,
        [fieldName]: onlyDate ? date.toDateString() : date.toISOString(),
      });

      const changedField = fields.find((field) => field.name === fieldName);
      if (changedField && changedField.onChange) {
        changedField.onChange(date, true); // Pass true for now, real validation on blur
      }
      handleBlur(fieldName);
    };

    const handleSelectChange = (fieldName, value, callback) => {
      setShowCurrentInputError(false);
      setEditingData({
        ...editingData,
        [fieldName]: value,
      });

      if (callback) {
        callback({
          ...editingData,
          [fieldName]: value,
        });
      }
    };

    const handleRemoveExternalError = (fieldName) => {
      if (!validationErrors?.[fieldName]) return;
      if (onSetExternalError) onSetExternalError(fieldName, undefined);
    };

    const handleBlur = async (fieldName) => {
      setCurrentInputName(fieldName);
      let updatedEditedFields = editedFields;
      handleRemoveExternalError(fieldName);

      if (editedFields.indexOf(fieldName) === -1) {
        updatedEditedFields = [...editedFields, fieldName];
        setEditedFields(updatedEditedFields);
      }

      await validateFields(updatedEditedFields, true);
    };

    const handleEnterKeyPress = useCallback(
      async (event) => {
        event.preventDefault();
        if (allowEnterSubmit && ['Enter', 'Tab'].includes(event.key)) {
          const validData = await getValidDataInternal();

          if (!validData) {
            setShowCurrentInputError(true);
          } else if (onSave) {
            onSave(validData);
          }
        }
      },
      [allowEnterSubmit, getValidDataInternal, onSave]
    );

    const firstEditableIndex = fields.findIndex((field) => field.type === 'text' && !field.disabled);

    const gridItems = fields.map((field, i) => {
      const autoFocus = useAutoFocus ? i === firstEditableIndex : false;
      let fieldComponent = null;
      const label = useLabel ? '' : field.label;
      let helperTextArray;

      const showAnyError = (showCurrentInputError && currentInputName === field.name) || showAllErrors;

      if (errors && errors[field.name] && showAnyError) {
        helperTextArray = errors[field.name];
      }

      const fieldValue = editingData ? editingData[field.name] : null;

      const helperText = helperTextArray ? helperTextArray[helperTextArray.length - 1] : null;
      const error = !!helperText;
      const commonProps = {
        FormHelperTextProps: {
          className: `
          ${classes.helperText} ${field.helperTextPosition === 'right' ? classes.rightHelperText : ''} 
          ${field.resetHelperText ? classes.resetHelperText : ''} 
          ${field?.helperTextClasses || ''}
          `,
        },
        autoComplete: field.autoComplete || 'off',
        autoFocus,
        className: `${useLabel ? '' : classes.field} ${field.className}`,
        classes: field.classes,
        disabled: field.disabled || saving,
        error,
        fullWidth: true,
        helperText,
        inputProps: { maxLength: field.maxLength },
        key: field.name,
        label,
        margin: field.margin,
        maxRows: field.rowsMax,
        onBlur: () => handleBlur(field.name),
        placeholder: field.placeholder,
        required: field.required,
        rows: field.rows,
        size: field.size,
        value: fieldValue || '',
        variant: field.variant ? field.variant : 'standard',
      };

      if (field.type === 'text') {
        fieldComponent = (
          <TextField
            {...commonProps}
            multiline={field.multiline}
            minRows={field.rows}
            onChange={(event) => handleTextChange(field.name, event)}
            onBlur={(event) => handleTextChange(field.name, event)}
            onKeyUp={handleEnterKeyPress}
          />
        );
      }

      if (field.type === 'password') {
        fieldComponent = (
          <PasswordTextField {...commonProps} onChange={(event) => handleTextChange(field.name, event)} onKeyUp={handleEnterKeyPress} hidePasswordIcon={field.hidePasswordIcon} />
        );
      }

      if (field.type === 'select') {
        fieldComponent = (
          <SelectField
            {...commonProps}
            multiple={field.multiple}
            items={field.items}
            renderValue={field.renderValue}
            onChange={(value) => handleSelectChange(field.name, value, field.onChange)}
          />
        );
      }

      if (field.type === 'color-select') {
        fieldComponent = <ColorSelectField {...commonProps} items={field.items} onChange={(value) => handleSelectChange(field.name, value)} />;
      }

      if (field.type === 'image-upload') {
        fieldComponent = (
          <ImageUploadButton
            imageUrl={fieldValue}
            onChange={(value) => handleSelectChange(field.name, value)}
            error={helperText}
            validation={field.validation}
            errorMessage={field.errorMessage}
          />
        );
      }

      if (field.type === 'date') {
        fieldComponent = (
          <KeyboardDatePicker {...commonProps} format="MM/DD/YYYY" value={fieldValue || null} onChange={(value) => handleDateChange(field.name, value, !!field.onlyDate)} />
        );
      }

      if (field.type === 'subdomain') {
        fieldComponent = <SubdomainField {...commonProps} onChange={(event) => handleTextChange(field.name, event)} onKeyUp={handleEnterKeyPress} />;
      }

      if (field.type === 'phone') {
        fieldComponent = <MaterialUiPhoneNumber defaultCountry="us" {...commonProps} onChange={(value) => handlePhoneChange(field.name, value)} />;
      }

      if (!fieldComponent) {
        return null;
      }

      if (useLabel) {
        if (labelPosition === 'top') {
          const labelContent = field.customLabel || (
            <>
              {field.label}
              {field.required && <span style={{ color: '#B12A2A' }}> *</span>}
            </>
          );

          return (
            <Grid container className={`${classes.gridRow} ${field.containerClass || ''}`} style={{ ...(field.style || {}), width: field.width }} key={field.name}>
              <Grid item xs={12}>
                <Typography align="left" className={`${labelClass} field-label`}>
                  {labelContent}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {fieldComponent}
              </Grid>
              {field.message && (
                <Typography align="left" className={classes.message}>
                  {field.message}
                </Typography>
              )}
            </Grid>
          );
        }
        if (labelPosition === 'left') {
          return (
            <Grid container spacing={2} className={classes.gridRow} key={field.name}>
              <Grid item xs={3} className={classes.gridLabel}>
                <Typography align="right" className={`${labelClass} field-label`}>
                  {field.label}
                </Typography>
              </Grid>
              <Grid item xs={8}>
                {fieldComponent}
              </Grid>
            </Grid>
          );
        }
        return (
          <Grid container spacing={2} className={classes.gridRow} key={field.name} direction="column">
            <Grid item>
              <Typography align="left" className={`${labelClass} field-label`}>
                {field.label}
              </Typography>
            </Grid>
            <Grid item>{fieldComponent}</Grid>
          </Grid>
        );
      }

      return fieldComponent;
    });

    return gridItems;
  }
);
